import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { CATEGORIES, ZONES, POPULAR_MEDICINES } from "@/lib/constants";
import { isNew } from "@/lib/dates";
import { Search, Target, MessageCircle, MapPin, Plus, Clock, Trash2, CheckCircle2, Sparkles, Zap, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import MedicineSelector from "@/components/MedicineSelector";

const URGENCY = {
  urgente: { text: "URGENTE", className: "bg-rose-600 text-white" },
  pronto:  { text: "Pronto",  className: "bg-amber-500 text-white" },
  normal:  { text: "Normal",  className: "bg-slate-500 text-white" },
};

export default function MedicineRequestsListPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("todos");
  const [zone, setZone] = useState("todas");
  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("all"); // all | mine

  const goToRequest = (name, cat) => {
    const qs = new URLSearchParams({ prefill: name });
    if (cat) qs.set("category", cat);
    const target = `/buscar-medicamento?${qs.toString()}`;
    if (!user || user === false) {
      toast.info("Inicia sesión para publicar tu búsqueda");
      navigate("/login", { state: { from: target } });
      return;
    }
    navigate(target);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      if (tab === "mine") {
        const { data } = await api.get("/medicine-requests/mine");
        setItems(data || []);
      } else {
        const params = {};
        if (category !== "todos") params.category = category;
        if (zone !== "todas") params.zone = zone;
        if (search.trim()) params.search = search.trim();
        const { data } = await api.get("/medicine-requests", { params });
        setItems(data || []);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [category, zone, tab]);
  useEffect(() => {
    const t = setTimeout(() => { if (tab !== "mine") fetchAll(); }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const removeRequest = async (id) => {
    if (!confirm("¿Eliminar esta búsqueda?")) return;
    try { await api.delete(`/medicine-requests/${id}`); fetchAll(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const markFulfilled = async (id) => {
    try {
      await api.post(`/medicine-requests/${id}/status`, { status: "fulfilled" });
      toast.success("Marcada como encontrada");
      fetchAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  return (
    <div className="bg-slate-50 min-h-[80vh]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6">
          <div className="flex items-start gap-3">
            <div className="h-12 w-12 rounded-xl bg-amber-100 grid place-items-center">
              <Target className="w-6 h-6 text-amber-700" />
            </div>
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-semibold text-blue-950">Tablero de búsquedas</h1>
              <p className="text-slate-600 mt-1 text-base">Personas buscando medicamentos. ¿Tienes alguno? Contáctalas directamente.</p>
            </div>
          </div>
          <Link to={user ? "/buscar-medicamento" : "/login"} data-testid="med-req-new-btn">
            <Button className="h-12 bg-blue-600 hover:bg-blue-700 rounded-xl">
              <Plus className="w-5 h-5 mr-1.5" /> Publicar búsqueda
            </Button>
          </Link>
        </div>

        {user && user !== false && (
          <div className="mb-4 flex gap-2">
            <Button variant={tab === "all" ? "default" : "outline"} onClick={() => setTab("all")} className={tab === "all" ? "bg-blue-600 hover:bg-blue-700" : ""} data-testid="med-req-tab-all">
              Todas
            </Button>
            <Button variant={tab === "mine" ? "default" : "outline"} onClick={() => setTab("mine")} className={tab === "mine" ? "bg-blue-600 hover:bg-blue-700" : ""} data-testid="med-req-tab-mine">
              Mis búsquedas
            </Button>
          </div>
        )}

        {/* Quick-request panel */}
        {tab === "all" && (
          <Card className="p-5 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white mb-5" data-testid="med-req-quick-panel">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-8 w-8 rounded-lg bg-amber-200 grid place-items-center">
                <Zap className="w-4 h-4 text-amber-800" />
              </div>
              <div>
                <h2 className="font-display text-base font-semibold text-amber-900">Búsqueda rápida</h2>
                <p className="text-xs text-amber-800">Toca un medicamento para publicar tu búsqueda con un solo clic.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2" data-testid="med-req-popular-grid">
              {POPULAR_MEDICINES.map((m) => (
                <button
                  key={m.name}
                  type="button"
                  onClick={() => goToRequest(m.name, m.category)}
                  className="text-left p-3 rounded-xl border border-amber-100 bg-white hover:border-amber-400 hover:bg-amber-50 transition-colors btn-press"
                  data-testid={`med-req-popular-${m.name}`}
                >
                  <div className="font-medium text-sm text-slate-900 leading-tight">{m.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center justify-between">
                    <span>{m.category}</span>
                    <ChevronRight className="w-3.5 h-3.5 text-amber-500" />
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-3 pt-3 border-t border-amber-100">
              <p className="text-xs text-amber-800 mb-2">¿No está en la lista? Elige del catálogo completo:</p>
              <MedicineSelector
                value=""
                category=""
                placeholder="Ver catálogo completo y elegir…"
                onSelect={({ medicine_name, category }) => goToRequest(medicine_name, category)}
              />
            </div>
          </Card>
        )}

        {tab === "all" && (
          <Card className="p-4 sm:p-5 rounded-2xl border border-slate-200 bg-white mb-5">
            <div className="grid sm:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input value={search} onChange={(e)=>setSearch(e.target.value)} placeholder="Buscar medicamento…" className="h-12 pl-9 bg-slate-50" data-testid="med-req-search-input" />
              </div>
              <Select value={category} onValueChange={setCategory}>
                <SelectTrigger className="h-12 bg-slate-50" data-testid="med-req-category-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todas las categorías</SelectItem>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={zone} onValueChange={setZone}>
                <SelectTrigger className="h-12 bg-slate-50" data-testid="med-req-zone-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="todas">Todas las zonas</SelectItem>
                  {ZONES.map((z) => <SelectItem key={z} value={z}>{z}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center text-slate-500 py-10">Cargando…</div>
        ) : items.length === 0 ? (
          <Card className="p-10 rounded-2xl border border-dashed border-slate-300 text-center" data-testid="med-req-empty">
            <Target className="w-10 h-10 text-amber-300 mx-auto mb-3" />
            <p className="text-slate-700 font-medium">{tab === "mine" ? "Aún no tienes búsquedas" : "Sin búsquedas activas"}</p>
            <p className="text-sm text-slate-500 mt-1">{tab === "mine" ? "Publica una para que la comunidad te ayude." : "Sé el primero en publicar una."}</p>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((r) => {
              const isOwner = user && user.id === r.created_by;
              const isFulfilled = r.status === "fulfilled";
              const wa = r.whatsapp.replace(/[^\d]/g, "");
              const waLink = `https://wa.me/${wa}?text=${encodeURIComponent(`Hola, vi tu búsqueda de ${r.medicine_name} en Medicamentos_CiudadEnfermeria. Te lo puedo ofrecer.`)}`;
              return (
                <Card key={r.id} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-col" data-testid={`med-req-card-${r.id}`}>
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <Badge className="bg-blue-100 text-blue-900 hover:bg-blue-100">{r.category}</Badge>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <Badge className={`${URGENCY[r.urgency]?.className} hover:${URGENCY[r.urgency]?.className}`}>
                        {URGENCY[r.urgency]?.text || r.urgency}
                      </Badge>
                      {isNew(r.created_at) && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-600 text-white text-[10px] font-bold">
                          <Sparkles className="w-3 h-3" /> NUEVO
                        </span>
                      )}
                      {isFulfilled && (
                        <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100">Encontrado</Badge>
                      )}
                    </div>
                  </div>

                  <h3 className="mt-3 font-display font-semibold text-lg text-blue-950 leading-tight">{r.medicine_name}</h3>
                  <div className="mt-1 text-sm text-slate-500">
                    <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5"/>{r.zone}</span>
                    {r.max_price > 0 && <span className="ml-3">hasta <strong className="text-blue-900">${r.max_price.toFixed(0)} MXN</strong></span>}
                  </div>

                  {r.notes && <p className="mt-3 text-sm text-slate-600 bg-slate-50 rounded-lg p-2 border border-slate-100">{r.notes}</p>}

                  <div className="mt-3 text-xs text-slate-500 flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5" /> {new Date(r.created_at).toLocaleDateString("es-MX")} · {r.created_by_name}
                  </div>

                  <div className="mt-4 pt-4 border-t border-slate-100 flex gap-2 flex-wrap">
                    {!isOwner && !isFulfilled && (
                      <a href={waLink} target="_blank" rel="noopener noreferrer" className="flex-1" data-testid={`med-req-wa-${r.id}`}>
                        <Button className="w-full bg-emerald-500 hover:bg-emerald-600 text-white">
                          <MessageCircle className="w-4 h-4 mr-1.5" /> Ofrecérselo
                        </Button>
                      </a>
                    )}
                    {isOwner && !isFulfilled && (
                      <Button variant="outline" size="sm" className="border-emerald-200 text-emerald-700 hover:bg-emerald-50" onClick={() => markFulfilled(r.id)} data-testid={`med-req-fulfill-${r.id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1"/> Lo encontré
                      </Button>
                    )}
                    {(isOwner || user?.role === "admin") && (
                      <Button variant="ghost" size="sm" className="text-rose-700 hover:bg-rose-50" onClick={() => removeRequest(r.id)} data-testid={`med-req-delete-${r.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
